/**
 * RiskTabContent Component
 *
 * Main content area for the Risk tab in Account settings.
 * Handles lazy loading of AccountRisk data, manages profile selection state,
 * and provides mutation handlers for profile CRUD operations.
 *
 * Includes profile description editing and cascade risk editor
 * (account default + strategy/playbook overrides).
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback } from 'react';

// 2. Workspace packages
import { ButtonGroup, Card, CardHeader, CardBody, Input, Spinner, TextArea } from '@enfomentis/ui';

// 3. Internal absolute imports
import type { AccountRisk, RiskProfile, RiskSpec } from '@/types/schema/AccountRisk';

// 4. Relative imports
import { useAccounts } from '../context';
import { CascadeEditor } from './CascadeEditor';
import { ProfileSelector } from './ProfileSelector';
import { resolveProfileId, toDisplayValue, toStoredValue, defaultValueForType } from './riskHelpers';

/**
 * RiskTabContent component
 */
export const RiskTabContent: React.FC = () => {
  const {
    accountRisk,
    setAccountRisk,
    riskLoading,
    ensureRiskLoaded,
    strategies,
    strategyPlaybooks,
    formState,
    activeBrokers,
  } = useAccounts();

  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Lazy-load risk data — reruns when selectedAccountId changes
  // (ensureRiskLoaded's own dep array includes selectedAccountId)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    void ensureRiskLoaded();
  }, [ensureRiskLoaded]);

  // ---------------------------------------------------------------------------
  // Revalidate selected profile when entity, active profile set, or default changes
  // Covers: entity change (full reset), profile archive/sync (revalidation),
  // default change when selection is invalid
  // ---------------------------------------------------------------------------
  const activeProfiles = accountRisk
    ? accountRisk.riskProfiles.filter(p => p.status === 'active')
    : [];
  const accountRiskId = accountRisk?.id;
  const activeProfileIds = activeProfiles.map(p => p.id).join(',');
  const currentDefaultId = accountRisk?.defaultProfileId;

  useEffect(() => {
    if (!selectedProfileId || !activeProfiles.some(p => p.id === selectedProfileId)) {
      setSelectedProfileId(resolveProfileId(accountRisk));
    }
    // Intentionally uses derived primitives, not full accountRisk object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountRiskId, activeProfileIds, currentDefaultId]);

  const handleAddProfile = useCallback((): void => {
    if (!accountRisk) return;
    const currentActive = accountRisk.riskProfiles.filter(p => p.status === 'active');
    const newId = crypto.randomUUID();
    const newProfile: RiskProfile = {
      id: newId,
      name: `Profile ${currentActive.length + 1}`,
      status: 'active',
      order: currentActive.length,
      accountDefault: { type: 'percentage', value: 0.01 },
    };
    setAccountRisk({
      ...accountRisk,
      riskProfiles: [...accountRisk.riskProfiles, newProfile],
    });
    setSelectedProfileId(newId);
  }, [accountRisk, setAccountRisk]);

  const handleDeleteProfile = useCallback((profileId: string): void => {
    if (!accountRisk) return;
    setAccountRisk({
      ...accountRisk,
      riskProfiles: accountRisk.riskProfiles.map(p =>
        p.id === profileId ? { ...p, status: 'archived' as const } : p
      ),
    });
    if (selectedProfileId === profileId) {
      const remaining = accountRisk.riskProfiles.filter(
        p => p.id !== profileId && p.status === 'active'
      );
      setSelectedProfileId(remaining[0]?.id ?? null);
    }
  }, [accountRisk, setAccountRisk, selectedProfileId]);

  const handleRenameProfile = useCallback((profileId: string, newName: string): void => {
    if (!accountRisk) return;
    setAccountRisk({
      ...accountRisk,
      riskProfiles: accountRisk.riskProfiles.map(p =>
        p.id === profileId ? { ...p, name: newName } : p
      ),
    });
  }, [accountRisk, setAccountRisk]);

  const handleSetDefault = useCallback((profileId: string | null): void => {
    if (!accountRisk) return;
    if (profileId) {
      setAccountRisk({ ...accountRisk, defaultProfileId: profileId });
    } else {
      // Remove defaultProfileId — cannot set to undefined with exactOptionalPropertyTypes
      const { defaultProfileId: _, ...rest } = accountRisk;
      setAccountRisk(rest as AccountRisk);
    }
  }, [accountRisk, setAccountRisk]);

  const handleUpdateDescription = useCallback((profileId: string, description: string): void => {
    if (!accountRisk) return;
    setAccountRisk({
      ...accountRisk,
      riskProfiles: accountRisk.riskProfiles.map(p => {
        if (p.id !== profileId) return p;
        if (description) return { ...p, description };
        // Remove description field when empty
        const { description: _, ...withoutDesc } = p;
        return withoutDesc as RiskProfile;
      }),
    });
  }, [accountRisk, setAccountRisk]);

  const canDeleteProfile = useCallback((profileId: string): boolean => {
    if (!accountRisk) return false;
    const active = accountRisk.riskProfiles.filter(p => p.status === 'active');
    if (active.length <= 1) return false;
    if (accountRisk.defaultProfileId === profileId) return false;
    return true;
  }, [accountRisk]);

  const handleUpdateProfile = useCallback((profileId: string, updatedProfile: RiskProfile): void => {
    if (!accountRisk) return;
    setAccountRisk({
      ...accountRisk,
      riskProfiles: accountRisk.riskProfiles.map(p =>
        p.id === profileId ? updatedProfile : p
      ),
    });
  }, [accountRisk, setAccountRisk]);

  // ---------------------------------------------------------------------------
  // Account Default Risk handlers (used by sibling row in CardHeader)
  // ---------------------------------------------------------------------------
  const handleAccountDefaultInputChange = useCallback((rawValue: string): void => {
    if (!accountRisk || !selectedProfileId) return;
    const selectedProf = accountRisk.riskProfiles.find(p => p.id === selectedProfileId);
    if (!selectedProf) return;
    const inputValue = rawValue === '' ? 0 : parseFloat(rawValue);
    if (isNaN(inputValue)) return;
    const updatedRisk: RiskSpec = {
      ...selectedProf.accountDefault,
      value: toStoredValue(inputValue, selectedProf.accountDefault.type),
    };
    handleUpdateProfile(selectedProfileId, { ...selectedProf, accountDefault: updatedRisk });
  }, [accountRisk, selectedProfileId, handleUpdateProfile]);

  const handleAccountDefaultTypeToggle = useCallback((isFixed: boolean): void => {
    if (!accountRisk || !selectedProfileId) return;
    const selectedProf = accountRisk.riskProfiles.find(p => p.id === selectedProfileId);
    if (!selectedProf) return;
    const newType = isFixed ? 'fixed' as const : 'percentage' as const;
    const updatedRisk: RiskSpec = { type: newType, value: defaultValueForType(newType) };
    handleUpdateProfile(selectedProfileId, { ...selectedProf, accountDefault: updatedRisk });
  }, [accountRisk, selectedProfileId, handleUpdateProfile]);

  // ---------------------------------------------------------------------------
  // Derived: selected profile and effective currency for cascade editor
  // ---------------------------------------------------------------------------
  const selectedProfile = selectedProfileId
    ? activeProfiles.find(p => p.id === selectedProfileId) ?? null
    : null;

  // Same resolution chain as AccountsPage: account override → broker default → USD
  const brokerCurrency = activeBrokers
    .find(b => b.id === formState.brokerId)
    ?.feeMetadata?.defaultAccountCurrency;
  const currency = formState.baseCurrency || brokerCurrency || 'USD';

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Loading state
  if (riskLoading && !accountRisk) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" label="Loading risk profiles..." />
      </div>
    );
  }

  // No data (shouldn't happen after ensureRiskLoaded, but guard)
  if (!accountRisk) {
    return (
      <div className="flex items-center justify-center py-12 text-center">
        <p className="text-sm text-text-tertiary">
          No risk configuration available
        </p>
      </div>
    );
  }

  return (
    <Card shadow="none" radius="none" className="flex-1 min-h-0 flex flex-col">
      <CardHeader className="flex flex-col items-stretch px-0 py-0 pb-0">
        {/* Profile selector — tabs only */}
        <ProfileSelector
          profiles={activeProfiles}
          selectedProfileId={selectedProfileId}
          defaultProfileId={accountRisk.defaultProfileId ?? null}
          onSelectProfile={setSelectedProfileId}
          onAddProfile={handleAddProfile}
          onDeleteProfile={handleDeleteProfile}
          onRenameProfile={handleRenameProfile}
          onSetDefault={handleSetDefault}
          canDelete={canDeleteProfile}
        />

        {/* Meta row: Description + Account Default Risk */}
        {selectedProfile && selectedProfileId && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
            <TextArea
              label="Description"
              labelPlacement="outside-left"
              placeholder="e.g., 'Clean setup, no negative confluences'"
              value={selectedProfile.description || ''}
              onValueChange={(value) => handleUpdateDescription(selectedProfileId, value)}
              minRows={1}
              maxRows={3}
              size="sm"
              classNames={{
                base: 'flex-1',
                inputWrapper: 'w-full',
              }}
            />

            <div className="flex items-center gap-3 sm:ml-auto shrink-0">
              <Input
                type="number"
                step="any"
                label="Risk"
                labelPlacement="outside-left"
                value={toDisplayValue(selectedProfile.accountDefault)}
                onChange={(e) => handleAccountDefaultInputChange(e.target.value)}
                min="0"
                {...(selectedProfile.accountDefault.type === 'percentage' ? { max: '100' } : {})}
                classNames={{
                  label: 'text-sm',
                  input: 'text-right ',
                }}
                size="sm"
              />
              <ButtonGroup
                options={[
                  { value: 'percentage', label: '%' },
                  { value: 'fixed', label: currency },
                ]}
                value={selectedProfile.accountDefault.type}
                onChange={(val) => handleAccountDefaultTypeToggle(val === 'fixed')}
                size="sm"
                variant="flat"
              />
            </div>
          </div>
        )}
      </CardHeader>

      <CardBody className="flex-1 min-h-0 flex flex-col px-0 py-1">
        {/* Cascade editor — scrolls */}
        {selectedProfile && selectedProfileId && (
          <CascadeEditor
            profile={selectedProfile}
            strategies={strategies}
            strategyPlaybooks={strategyPlaybooks}
            onUpdateProfile={(p) => handleUpdateProfile(selectedProfileId, p)}
            currency={currency}
          />
        )}
      </CardBody>
    </Card>
  );
};
